import { DenormalizedCustomizableGroup, DenormalizedCustomizableQuestion } from '@packages/types'
import classNames from 'classnames'
import React, { useEffect } from 'react'

import { useDispatch, useSelector } from 'customizer/hooks'
import * as questionPanelActions from 'customizer/questionPanel/actions'
import * as questionPanelSelectors from 'customizer/questionPanel/selectors'
import * as stocksSelectors from 'customizer/stocks/selectors'
import useThemeSettings from 'customizer/theme/useThemeSettings'
import ArrowLeftBackButton from 'icons/bold/52-Arrows-Diagrams/01-Arrows/arrow-left-1.svg'
import ArrowLeft from 'icons/custom/arrow-left.svg'
import ArrowRight from 'icons/custom/arrow-right.svg'
import ErrorMessageCard from 'themes/common/components/ErrorMessageCard'
import OutOfStockMessageCard from 'themes/common/components/OutOfStockMessageCard'
import SubmitAction from 'themes/common/components/SubmitAction'
import Translation from 'themes/common/components/Translation'
import SummaryDrawerButton from 'themes/common/components/summaryDrawer/SummaryDrawerButton'
import useOutOfStockMessageCard from 'themes/common/hooks/useOutOfStockMessageCard'
import useShouldPreventForwardNavigation from './../../hooks/useShouldPreventForwardNavigation'

import './ActionSection.scss'

export interface ActionSectionProps {
  steps: (DenormalizedCustomizableGroup | DenormalizedCustomizableQuestion)[]
  selectedStepId: string
  requiredQuestions: string[]
  selectPreviousStep: () => void
  selectNextStep: () => void
  selectLastStep: () => void
  isFinishingCustomization?: boolean
  children?: React.ReactNode
}

const ActionSection = ({
  steps,
  selectedStepId,
  selectPreviousStep,
  selectNextStep,
  selectLastStep,
  isFinishingCustomization,
  children,
}: ActionSectionProps) => {
  const dispatch = useDispatch()
  const isSubmitting = useSelector(questionPanelSelectors.isSubmittingSelector)
  const showSubmitError = useSelector(questionPanelSelectors.showSubmitErrorSelector)
  const shouldPreventForwardNavigation = useShouldPreventForwardNavigation()

  const { questionFontColor } = useThemeSettings('question')
  const questionPanelStyle = useThemeSettings('questionPanel')
  const { backgroundColor: finishCustomizationBackgroundColor } = useThemeSettings('customizer')
  const isSelectionOutOfStock = useSelector(stocksSelectors.isSelectionOutOfStockSelector)
  const { setShowOutOfStockMessageCard, setShowOutOfStockMessageCardWithTimeout, showOutOfStockMessageCard } =
    useOutOfStockMessageCard(isSelectionOutOfStock)

  useEffect(() => {
    setShowOutOfStockMessageCardWithTimeout()
  }, [isFinishingCustomization])

  const isFirstStepSelected = steps[0]?.id === selectedStepId

  return (
    <div
      className={classNames('action-section', {
        'action-section--finishing-customization': isFinishingCustomization,
      })}
      data-testid="action-section"
      style={
        isFinishingCustomization
          ? { backgroundColor: finishCustomizationBackgroundColor }
          : {
              borderTopColor: questionPanelStyle.mobileNavBorderColor,
              backgroundColor: questionPanelStyle.mobileNavBackgroundColor,
            }
      }
    >
      {children}

      {showOutOfStockMessageCard && <OutOfStockMessageCard onClick={() => setShowOutOfStockMessageCard(false)} />}

      {isFinishingCustomization ? (
        <>
          {showSubmitError && (
            <div onClick={() => dispatch(questionPanelActions.hideSubmitError())}>
              <ErrorMessageCard
                className="add-to-cart-error__message"
                message={<span>There was an error adding your product to the cart, please try again.</span>}
              />
            </div>
          )}

          {!isSubmitting && (
            <button className="finish-customization__back-button" onClick={selectLastStep}>
              <ArrowLeftBackButton style={{ fill: questionFontColor }} />
              <Translation field="goBack" style={{ color: questionFontColor }} />
            </button>
          )}

          <SubmitAction
            className={classNames('action-section__main-button', {
              'action-section__main-button--last-step': isFinishingCustomization,
            })}
            loading="circularCircle"
            showOutOfStockMessageCard={showOutOfStockMessageCard}
            setShowOutOfStockMessageCard={setShowOutOfStockMessageCard}
            setShowOutOfStockMessageCardWithTimeout={setShowOutOfStockMessageCardWithTimeout}
            showSummary
          />
        </>
      ) : (
        <div className="action-section__navigation">
          {!isFirstStepSelected && (
            <button
              className="action-section__navigation-arrow"
              onClick={selectPreviousStep}
              aria-label="previous step"
              style={{
                borderRadius: questionPanelStyle.mobileNavButtonsBorderRadius,
                backgroundColor: questionPanelStyle.mobileNavPrevButtonBackgroundColor,
                borderColor: questionPanelStyle.mobileNavPrevButtonBorderColor,
              }}
            >
              <ArrowLeft
                style={{
                  stroke: questionPanelStyle.mobileNavPrevButtonIconColor,
                }}
              />
            </button>
          )}

          <SummaryDrawerButton steps={steps} selectedStepId={selectedStepId} />

          <button
            className="action-section__navigation-arrow action-section__navigation-arrow--right"
            onClick={selectNextStep}
            disabled={shouldPreventForwardNavigation}
            aria-label="next step"
            style={{
              borderRadius: questionPanelStyle.mobileNavButtonsBorderRadius,
              backgroundColor: questionPanelStyle.mobileNavNextButtonBackgroundColor,
              borderColor: questionPanelStyle.mobileNavNextButtonBorderColor,
            }}
          >
            <ArrowRight
              style={{
                stroke: questionPanelStyle.mobileNavNextButtonIconColor,
              }}
            />
          </button>
        </div>
      )}
    </div>
  )
}

export default ActionSection
